import { isAxiosError } from "axios";
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Spinner } from "@/atoms";
import { useUser } from "@/modules/user";
import { PersonalisationUserLoadError } from "modules/personalisation/components/PersonalisationUserLoadError";

export const PersonalisationUserGuard = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading, isError, error } = useUser();
  const userId = user?.id;

  const axiosStatus = isAxiosError(error) ? error.response?.status : undefined;
  const isUnauthorized = axiosStatus === 401 || axiosStatus === 403;

  const hasLoggedRef = useRef(false);
  useEffect(() => {
    // Log error for irrecoverable user-load failures.
    // We intentionally do NOT log for:
    // - loading states (common due to stale-time refetch)
    // - missing user without an API error (we redirect to /signin)
    // - 401/403 (expected when user is logged out)
    if (!isError) return;
    if (isUnauthorized) return;
    if (hasLoggedRef.current) return;

    hasLoggedRef.current = true;
    console.error("[Personalisation] Route loaded without a logged in user", {
      pathname: location.pathname,
      search: location.search,
      isError,
      axiosStatus,
    });
  }, [axiosStatus, isError, isUnauthorized, location.pathname, location.search]);

  useEffect(() => {
    if (isLoading) return;
    if (userId) return;
    if (isError && isUnauthorized) {
      navigate("/signin", { replace: true });
      return;
    }
    if (!isError && !userId) {
      navigate("/signin", { replace: true });
    }
  }, [isError, isLoading, isUnauthorized, navigate, userId]);

  if (isLoading) {
    return <Spinner />;
  }

  if (userId) {
    return children;
  }

  if (isError && !isUnauthorized) {
    return <PersonalisationUserLoadError status={axiosStatus} />;
  }

  // Redirect effect above will handle this state.
  return null;
};
